import { Injectable } from '@angular/core';
import { Action, Selector, State, StateToken, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { Undoable } from '@medsurf/flat-decorators';
import { HistoryEntityBaseState } from '../base/history-entity.base.state';

export const ANNOTATION_TARGET_ENTITY_TOKEN = new StateToken<AnnotationTargetEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.ANNOTATION_TARGET_ENTITY);

export type AnnotationTargetEntityStateModel = FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<FlatModels.AnnotationTargetEntityModels.AnnotationTarget>;

@State<AnnotationTargetEntityStateModel>({
  name: ANNOTATION_TARGET_ENTITY_TOKEN,
  defaults: {
    snapshots: {},
    entities: {},
  }
})
@Injectable()
export class AnnotationTargetEntityState extends HistoryEntityBaseState<FlatModels.AnnotationTargetEntityModels.AnnotationTarget, AnnotationTargetEntityStateModel> {
  //<editor-fold desc="Selectors">

  @Selector([AnnotationTargetEntityState])
  static override typedEntities$(state: AnnotationTargetEntityStateModel): { [id: string]: FlatModels.AnnotationTargetEntityModels.AnnotationTarget } {
    return HistoryEntityBaseState.typedEntities$(state);
  }

  @Selector([AnnotationTargetEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.AnnotationTargetEntityModels.AnnotationTarget }): FlatModels.AnnotationTargetEntityModels.AnnotationTarget[] {
    return HistoryEntityBaseState.entities$(typedEntities);
  }

  @Selector([AnnotationTargetEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.AnnotationTargetEntityModels.AnnotationTarget }) {
    return HistoryEntityBaseState.entity$(typedEntities);
  }

  @Selector([AnnotationTargetEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.AnnotationTargetEntityModels.AnnotationTarget }) {
    return HistoryEntityBaseState.entitiesByIds$(typedEntities);
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">

  @Action(FlatActions.AnnotationTargetEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<AnnotationTargetEntityStateModel>,
                            action: FlatActions.AnnotationTargetEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }

  @Action(FlatActions.AnnotationTargetEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<AnnotationTargetEntityStateModel>,
                              action: FlatActions.AnnotationTargetEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }

  @Action(FlatActions.AnnotationTargetEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<AnnotationTargetEntityStateModel>,
                               action: FlatActions.AnnotationTargetEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }

  @Action(FlatActions.AnnotationTargetEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<AnnotationTargetEntityStateModel>,
                                 action: FlatActions.AnnotationTargetEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">

  @Action(FlatActions.AnnotationTargetEntityActions.AddEntity)
  @Undoable(
    FlatActions.AnnotationTargetEntityActions.AddEntity,
    ANNOTATION_TARGET_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD
  )
  override addEntity(state: StateContext<AnnotationTargetEntityStateModel>,
                     action: FlatActions.AnnotationTargetEntityActions.AddEntity) {
    return super.addEntity(state, action);
  }

  @Action(FlatActions.AnnotationTargetEntityActions.UpdateEntity)
  @Undoable(
    FlatActions.AnnotationTargetEntityActions.UpdateEntity,
    ANNOTATION_TARGET_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE
  )
  override updateEntity(state: StateContext<AnnotationTargetEntityStateModel>,
                        action: FlatActions.AnnotationTargetEntityActions.UpdateEntity) {
    return super.updateEntity(state, action);
  }

  @Action(FlatActions.AnnotationTargetEntityActions.RemoveEntity)
  @Undoable(
    FlatActions.AnnotationTargetEntityActions.RemoveEntity,
    ANNOTATION_TARGET_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE
  )
  override removeEntity(state: StateContext<AnnotationTargetEntityStateModel>,
                        action: FlatActions.AnnotationTargetEntityActions.RemoveEntity) {
    return super.removeEntity(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Snapshot">

  @Action(FlatActions.AnnotationTargetEntityActions.UndoSnapshot)
  override undoSnapshot(state: StateContext<AnnotationTargetEntityStateModel>,
                        action: FlatActions.AnnotationTargetEntityActions.UndoSnapshot) {
    return super.undoSnapshot(state, action);
  }

  @Action(FlatActions.AnnotationTargetEntityActions.RedoSnapshot)
  override redoSnapshot(state: StateContext<AnnotationTargetEntityStateModel>,
                        action: FlatActions.AnnotationTargetEntityActions.RedoSnapshot) {
    return super.redoSnapshot(state, action);
  }

  @Action(FlatActions.AnnotationTargetEntityActions.CleanUpSnapshots)
  override cleanUpSnapshots(state: StateContext<AnnotationTargetEntityStateModel>,
                            action: FlatActions.AnnotationTargetEntityActions.CleanUpSnapshots) {
    return super.cleanUpSnapshots(state, action);
  }

  //</editor-fold>

  //</editor-fold>
}
